import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import {
  deleteUser,
  listUsers,
  resetUserPassword,
  toggleUserActive,
  type AdminUser,
} from '@/api/admin';
import { formatDate } from '@/lib/format';
import { UserFormModal } from './UserFormModal';
import { UserPermissionsModal } from './UserPermissionsModal';

export function UsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const { user: me, logout } = useAuth();
  const [openId, setOpenId] = useState<number | 'new' | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [permTarget, setPermTarget] = useState<AdminUser | null>(null);
  const [newPwd, setNewPwd] = useState('');

  const query = useQuery({ queryKey: ['admin-users'], queryFn: listUsers });
  const rows = query.data ?? [];

  const onErr = (e: unknown) => {
    toast.push(apiError(e, 'Failed'), 'error');
  };
  const toggleMut = useMutation({
    mutationFn: toggleUserActive,
    onSuccess: () => {
      toast.push('Status changed', 'success');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: onErr,
  });
  const delMut = useMutation({
    mutationFn: deleteUser,
    onSuccess: (_data, id) => {
      toast.push('User removed', 'success');
      if (id === me?.id) {
        logout();
      } else {
        qc.invalidateQueries({ queryKey: ['admin-users'] });
      }
    },
    onError: onErr,
  });
  const resetMut = useMutation({
    mutationFn: () => resetUserPassword(resetTarget!.id, newPwd),
    onSuccess: () => {
      toast.push(`Password reset for ${resetTarget?.username}`, 'success');
      setResetTarget(null);
      setNewPwd('');
    },
    onError: onErr,
  });

  return (
    <div>
      <SectionHeader
        title="User Management"      />

      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={() => setOpenId('new')}
          className="inline-flex items-center gap-1 rounded-lg bg-accent px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-xs"
        >
          ＋ New User
        </button>
      </div>

      <Table<AdminUser>
        storageKey="users"
        title="Users"
        count={`${rows.length} users`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'name', header: 'Full Name', cell: (r) => <strong>{r.fullName}</strong> },
          { key: 'user', header: 'Username', cell: (r) => r.username },
          { key: 'email', header: 'Email', cell: (r) => r.email ?? '—' },
          { key: 'role', header: 'Role', cell: (r) => <Badge tone="accent">{r.role}</Badge> },
          { key: 'dept', header: 'Department', cell: (r) => {
            if (!r.department) return '—';
            const depts = r.department.split(',').map((d) => d.trim()).filter(Boolean);
            return (
              <div className="flex flex-wrap gap-1">
                {depts.map((d) => (
                  <span key={d} className="rounded bg-accent/15 px-1.5 py-0.5 text-xs font-semibold text-accent">{d}</span>
                ))}
              </div>
            );
          }},
          {
            key: 'status',
            header: 'Status',
            cell: (r) => (
              <Badge tone={r.status === 'Active' ? 'success' : 'muted'}>{r.status}</Badge>
            ),
          },
          {
            key: 'last',
            header: 'Last Login',
            cell: (r) => (r.lastLoginAt ? formatDate(r.lastLoginAt) : 'Never'),
          },
          {
            key: 'actions',
            header: '',
            width: '280px',
            cell: (r) => (
              <div className="flex flex-wrap gap-1">
                <Button variant="glass" size="sm" onClick={() => setOpenId(r.id)}>
                  Edit
                </Button>
                {r.roleCode !== 'SUPER_ADMIN' && (
                  <Button variant="glass" size="sm" onClick={() => setPermTarget(r)} title="Override this user's module access">
                    Permissions
                  </Button>
                )}
                <Button variant="glass" size="sm" onClick={() => setResetTarget(r)}>
                  Reset PW
                </Button>
                {r.roleCode !== 'SUPER_ADMIN' && (
                  <>
                    <Button
                      variant="glass"
                      size="sm"
                      onClick={() => toggleMut.mutate(r.id)}
                      disabled={toggleMut.isPending}
                    >
                      {r.status === 'Active' ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={async () => {
                        if (await confirmDlg(`Delete ${r.fullName}?`)) delMut.mutate(r.id);
                      }}
                      disabled={delMut.isPending}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </div>
            ),
          },
        ]}
      />

      <UserFormModal
        open={openId !== null}
        userId={openId === 'new' ? null : openId}
        onClose={() => setOpenId(null)}
      />

      <UserPermissionsModal
        open={!!permTarget}
        user={permTarget}
        onClose={() => setPermTarget(null)}
      />

      {resetTarget && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div data-compact-form="true" className="w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 text-[var(--text-primary)] shadow-2xl backdrop-blur-xl">
            <div className="mb-3 text-sm font-extrabold">
              Reset password for {resetTarget.username}
            </div>
            <input
              type="password"
              autoFocus
              placeholder="New password (min 8 chars)"
              className="w-full rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] p-2 text-sm text-[var(--text-primary)] outline-none focus:border-accent"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="glass"
                size="sm"
                onClick={() => {
                  setResetTarget(null);
                  setNewPwd('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => resetMut.mutate()}
                disabled={newPwd.length < 8 || resetMut.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
